import { Trans, useTranslation } from "react-i18next";
import { FaCircleCheck, FaCircleXmark } from "react-icons/fa6";
import { MdDownload } from "react-icons/md";
import { Link } from "@tanstack/react-router";
import { css } from "../../../styled-system/css";
import { Flex, VStack } from "../../../styled-system/jsx";
import { Button } from "../../components/core/basic/Button";
import Loading from "../../components/core/blocks/Loading";
import ProgressBar from "../../components/core/basic/ProgressBar";
import { Accordion } from "../../components/core/basic/Accordion";
import { useConfirmUpload } from "./api/useConfirmUpload";
import { useUpload } from "./api/useUpload";
import { UploadStatus, getStatusLabel, isTerminalStatus } from "./constants";
import { flattenUploadAlerts } from "./alertUtils";
import { AlertSection } from "./AlertSection";
import { IgnoreWarningsConfirmation } from "./IgnoreWarningsConfirmation";
import { downloadBulkUploadErrorsAndWarnings } from "./downloadAlerts";

interface UploadStatusViewProps {
  uploadId: number;
  onClose: () => void;
  successMessage: string;
  confirmationPhrase: string;
  showUploadsPageLink?: boolean;
}

export function UploadStatusView({
  uploadId,
  onClose,
  successMessage,
  confirmationPhrase,
  showUploadsPageLink = true,
}: UploadStatusViewProps) {
  const { t } = useTranslation(["uploads", "common"]);
  const { data: upload, isLoading } = useUpload(uploadId);
  const { confirmProcess, ignoreWarnings, isPending } = useConfirmUpload(uploadId);

  if (isLoading || !upload) {
    return <Loading fullPage={false} height="200px" backgroundColor="inherit" />;
  }

  if (!isTerminalStatus(upload.status)) {
    return (
      <VStack gap="medium" alignItems="center" paddingY="xlarge">
        <Loading fullPage={false} height="80px" backgroundColor="inherit" />
        <p className={css({ fontSize: "medium", fontWeight: "semibold" })}>
          {t(($) => $.uploads.view["Status: {status}"], { status: t(getStatusLabel(upload.status)) })}
        </p>
        {upload.progress > 0 && <ProgressBar progress={upload.progress} width="100%" />}
        <p className={css({ fontSize: "small", color: "gray.500", textAlign: "center" })}>
          {showUploadsPageLink ?
            <Trans
              t={t}
              i18nKey={($) =>
                $.uploads.view[
                  "Your upload is processing in the background. You can safely close this dialog and track progress on the <uploadsLink>Uploads page</uploadsLink>."
                ]
              }
              components={{
                uploadsLink: (
                  <Link
                    to="/user/uploads"
                    className={css({ color: "trellisPalette.12", textDecoration: "underline" })}
                    onClick={onClose}
                  />
                ),
              }}
            />
          : t(($) => $.uploads.view["Your upload is processing. Please keep this dialog open until it completes."])}
        </p>
      </VStack>
    );
  }

  const { errors, warnings } = flattenUploadAlerts(upload);

  // Success
  if (upload.status === UploadStatus.SUCCESS) {
    return (
      <VStack gap="large" alignItems="stretch">
        <Flex gap="large" direction="column" justifyContent="center" alignItems="center">
          <FaCircleCheck className={css({ color: "trellisPalette.12", fontSize: 48 })} />
          <p>{successMessage}</p>
          <Button onPress={onClose}>{t(($) => $.common.actions.Close)}</Button>
        </Flex>
        {warnings.length > 0 && (
          <Accordion title={t(($) => $.uploads.view.warnings, { count: warnings.length })} marginTop="0">
            <Flex justifyContent="end" width="100%" marginBottom="small">
              <Button onPress={() => downloadBulkUploadErrorsAndWarnings(t, [], warnings)}>
                <MdDownload /> {t(($) => $.uploads.view["Download Warnings CSV"])}
              </Button>
            </Flex>
            <AlertSection type="warning" items={warnings} />
          </Accordion>
        )}
      </VStack>
    );
  }

  // Cancelled
  if (upload.status === UploadStatus.CANCELLED) {
    return (
      <Flex gap="large" direction="column" justifyContent="center" alignItems="center">
        <p className={css({ color: "gray.600" })}>{t(($) => $.uploads.view["This upload was cancelled."])}</p>
        <Button onPress={onClose}>{t(($) => $.common.actions.Close)}</Button>
      </Flex>
    );
  }

  // Validated
  if (upload.status === UploadStatus.VALIDATED) {
    return (
      <>
        {(errors.length > 0 || warnings.length > 0) && (
          <Flex justifyContent="end" width="100%">
            <Button onPress={() => downloadBulkUploadErrorsAndWarnings(t, errors, warnings)}>
              <MdDownload /> {t(($) => $.uploads.view["Download Errors & Warnings CSV"])}
            </Button>
          </Flex>
        )}
        <div className={css({ width: "100%", maxHeight: "400px", overflowY: "auto" })}>
          <VStack gap="large">
            {errors.length === 0 && warnings.length === 0 && (
              <VStack gap="medium" alignItems="center">
                <FaCircleCheck className={css({ color: "trellisPalette.12", fontSize: 32 })} />
                <p>{t(($) => $.uploads.view["Validation passed. Ready to process."])}</p>
                <Button onPress={() => confirmProcess()} isDisabled={isPending}>
                  {t(($) => $.uploads.view["Confirm & Process"])}
                </Button>
              </VStack>
            )}
            {errors.length > 0 && <AlertSection type="error" items={errors} />}
            {warnings.length > 0 && (
              <AlertSection type="warning" items={warnings}>
                {errors.length === 0 && (
                  <IgnoreWarningsConfirmation
                    confirmationPhrase={confirmationPhrase}
                    onConfirm={() => ignoreWarnings()}
                    isDisabled={isPending}
                  />
                )}
              </AlertSection>
            )}
          </VStack>
        </div>
      </>
    );
  }

  // Validation Failed — data issues the user needs to address
  if (upload.status === UploadStatus.VALIDATION_FAILED) {
    return (
      <VStack gap="large" alignItems="stretch">
        <Flex gap="large" direction="column" justifyContent="center" alignItems="center">
          <FaCircleXmark className={css({ color: "error", fontSize: 48 })} />
          <p>
            {t(($) => $.uploads.view["Validation failed. Please fix the issues below and upload a corrected file."])}
          </p>
          <Button onPress={onClose}>{t(($) => $.common.actions.Close)}</Button>
        </Flex>
        {(errors.length > 0 || warnings.length > 0) && (
          <Flex justifyContent="end" width="100%">
            <Button onPress={() => downloadBulkUploadErrorsAndWarnings(t, errors, warnings)}>
              <MdDownload /> {t(($) => $.uploads.view["Download Errors & Warnings CSV"])}
            </Button>
          </Flex>
        )}
        {errors.length > 0 && <AlertSection type="error" items={errors} />}
        {warnings.length > 0 && <AlertSection type="warning" items={warnings} />}
      </VStack>
    );
  }

  // Failed — server error, not actionable by the user
  const alertCount = errors.length + warnings.length;
  return (
    <VStack gap="large" alignItems="stretch">
      <Flex gap="large" direction="column" justifyContent="center" alignItems="center">
        <FaCircleXmark className={css({ color: "error", fontSize: 48 })} />
        <p>
          {t(($) => $.uploads.view["Processing failed due to a server error. Please try again or contact support."])}
        </p>
        <Button onPress={onClose}>{t(($) => $.common.actions.Close)}</Button>
      </Flex>
      {alertCount > 0 && (
        <Accordion
          title={t(($) => $.uploads.view["errors and warnings"], {
            errorCount: errors.length,
            warningCount: warnings.length,
          })}
          marginTop="0"
        >
          {errors.length > 0 && <AlertSection type="error" items={errors} />}
          {warnings.length > 0 && <AlertSection type="warning" items={warnings} />}
        </Accordion>
      )}
    </VStack>
  );
}
